import { useEffect, useMemo, useState } from 'preact/hooks';
import type { SupabaseClient } from '@supabase/supabase-js';
import { cargarCatalogo, crearProducto, type ProductoCosteado } from '../datos/catalogo';
import type { Ir } from '../rutas';
import { money, pct } from '../logica/dinero';
import { sinTildes } from '../logica/texto';

import { MARGEN_MIN } from './ProductoDetalle';
const TALLA: Record<string, string> = { NINO: 'Tallas de niño', ADULTO: 'Tallas de adulto', UNICA: 'Talla única', COBIJA: 'Cobija' };
const margenDe = (p: ProductoCosteado) => { const v = p.precios.COP; return v ? (v - p.costeo.total) / v : null; };

// Lista de productos por categoría con precio, costo y margen. Cada producto se abre en su ficha (fórmula, precios y datos).
export function Productos({ sb, ir, onCambio }: { sb: SupabaseClient; ir: Ir; onCambio: () => void }) {
  const [lista, setLista] = useState<ProductoCosteado[] | null>(null);
  const [error, setError] = useState('');
  const [buscar, setBuscar] = useState('');
  const [creando, setCreando] = useState(false);
  useEffect(() => { cargarCatalogo(sb).then(setLista).catch(e => setError(String(e.message || e))); }, []);

  const grupos = useMemo(() => {
    const t = sinTildes(buscar).trim();
    const vis = (lista || []).filter(p => !t || sinTildes(`${p.nombre} ${p.sku} ${p.categoria}`).includes(t));
    const g = new Map<string, ProductoCosteado[]>();
    for (const p of vis) { const k = p.categoria || 'Sin categoría'; if (!g.has(k)) g.set(k, []); g.get(k)!.push(p); }
    return [...g].sort(([a], [b]) => (a === 'Sin categoría' ? 1 : b === 'Sin categoría' ? -1 : a.localeCompare(b, 'es')));
  }, [lista, buscar]);

  if (error) return <section class="panel"><p class="error" role="alert">No se pudo leer el catálogo: {error}</p></section>;
  if (!lista) return <section class="panel"><p class="hint">Cargando el catálogo…</p></section>;
  const activos = lista.filter(p => p.activo);
  const incompletos = activos.filter(p => p.costeo.incompleto).length;
  const bajos = activos.filter(p => { const m = margenDe(p); return m != null && m < MARGEN_MIN; }).length;
  const sinPrecio = activos.filter(p => !p.precios.COP).length;

  return (
    <div>
      <div class="cabeza">
        <div>
          <h2>Productos y precios</h2>
          <p class="hint">El costo sale de la fórmula de cada prenda: materiales al promedio de compras y servicios a su costo. Toca un producto para editar su fórmula, sus precios y sus datos.</p>
        </div>
        <div class="fila-botones">
          <a class="btn" href="#/precios">Lista de precios</a>
          <button class="btn primary" type="button" disabled={creando} onClick={() => { setCreando(true); crearProducto(sb).then(sku => { onCambio(); ir('producto', sku); }, e => { setError(String((e as Error).message || e)); setCreando(false); }); }}>+ Nuevo producto</button>
        </div>
      </div>
      <input class="in buscar" type="search" placeholder="Buscar producto" aria-label="Buscar producto" value={buscar} onInput={e => setBuscar(e.currentTarget.value)} />
      <ul class="resumen">
        <li><b>{activos.length}</b> productos activos</li>
        <li><b>{incompletos}</b> con fórmula incompleta</li>
        <li><b>{bajos}</b> con margen bajo 50%</li>
        <li><b>{sinPrecio}</b> sin precio en pesos</li>
      </ul>
      {!grupos.length && <section class="panel"><p class="hint">{lista.length ? 'Ningún producto coincide con la búsqueda.' : 'Todavía no hay productos cargados.'}</p></section>}
      {grupos.map(([cat, ps]) => (
        <section class="panel flush" key={cat} aria-label={cat}>
          <h3 class="grupo">{cat} <span class="hint">{ps.length}</span></h3>
          <div class="tbl-wrap"><table class="tbl lista">
            <colgroup><col class="c-prod" /><col /><col /><col /><col /><col class="c-estado" /></colgroup>
            <thead><tr><th>Producto</th><th class="num">Precio COP</th><th class="num">Precio USD</th><th class="num">Costo</th><th class="num">Margen</th><th>Estado</th></tr></thead>
            <tbody>{ps.map(p => {
              const m = margenDe(p);
              return (
                <tr key={p.sku} class="click" onClick={() => ir('producto', p.sku)}>
                  <td class="prod"><a class="enlace-fila" href={'#/producto/' + p.sku} onClick={e => e.stopPropagation()}><strong>{p.nombre}</strong></a><span class="sub">{p.sku} · {TALLA[p.grupo_talla] || p.grupo_talla}</span></td>
                  <td class="num" data-l="Precio COP">{p.precios.COP ? money(p.precios.COP) : <span class="muted">Sin precio</span>}</td>
                  <td class="num" data-l="Precio USD">{p.precios.USD ? money(p.precios.USD, 'USD') : <span class="muted">—</span>}</td>
                  <td class="num" data-l="Costo">{money(p.costeo.total)}</td>
                  <td class={'num' + (m != null && m < MARGEN_MIN ? ' malo' : '')} data-l="Margen">{m != null ? pct(m) : '—'}</td>
                  <td class="estado-c"><div class="chips">
                    {!p.activo && <span class="pill">Inactivo</span>}
                    {p.costeo.incompleto && <span class="pill warn">Fórmula incompleta</span>}
                    {p.activo && m != null && m < MARGEN_MIN && <span class="pill warn">Margen bajo</span>}
                    {p.activo && !p.costeo.incompleto && m != null && m >= MARGEN_MIN && <span class="pill ok">OK</span>}
                  </div></td>
                </tr>
              );
            })}</tbody>
          </table></div>
        </section>
      ))}
    </div>
  );
}
